import { useState, useEffect, useRef, useMemo, useCallback } from "react";import { NavLink, Outlet, useLocation, useNavigate, Link } from "react-router-dom";
import { APP_VERSION } from "../version";
import { Gamepad2, LayoutDashboard, BarChart3, Settings, Trophy, Download, Menu, X, Star, Award, Database, Bell, LogOut, Volume2, VolumeX, Users, BookOpen, UserCog, Image, ShieldCheck, LinkIcon, Swords, Shield, Gift } from "lucide-react";
import { useAuth } from "@/components/ProtectedRoute";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import CommentActivityBell from "@/components/Community/CommentActivityBell";

const API = `${getApiUrl()}/api`;

// Notification sound - using a simple beep tone
const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800; // Hz
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
  } catch (e) {
    console.log("Could not play notification sound:", e);
  }
};

const navGroups = [
  {
    color: "#F59E0B", // amber — Dashboard
    items: [
      { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", hideKey: "dashboard" },
    ],
  },
  {
    color: "#3B82F6", // blue — Media
    items: [
      { to: "/browse", icon: BookOpen, label: "Browse Games", hideKey: "browse" },
      { to: "/games", icon: Gamepad2, label: "My Library" },
      { to: "/wishlist", icon: Gift, label: "Wish List", hideKey: "wishlist" },
      { to: "/priorities", icon: Star, label: "Priorities", hideKey: "priorities" },
      { to: "/multiplayer", icon: Swords, label: "Multiplayer", hideKey: "multiplayer" },
    ],
  },
  {
    color: "#6366F1", // indigo — Stats & Info
    items: [
      { to: "/achievements", icon: Award, label: "Achievements", hideKey: "achievements" },
      { to: "/statistics", icon: BarChart3, label: "Statistics", hideKey: "statistics" },
    ],
  },
  {
    color: "#A855F7", // purple — Sharing & Social
    items: [
      { to: "/signatures", icon: Image, label: "Signatures", hideKey: "signatures" },
      { to: "/notifications", icon: Bell, label: "Notifications", hideKey: "notifications" },
      { to: "/social", icon: Users, label: "Community Items", hideKey: "social" },
      { to: "/links", icon: LinkIcon, label: "Links", hideKey: "links" },
    ],
  },
  {
    color: "#10B981", // emerald — Maintenance
    items: [
      { to: "/import-export", icon: Download, label: "Import/Export", hideKey: "import-export", adminOnly: true },
      { to: "/settings", icon: Settings, label: "Settings" },
      { to: "/tutorials", icon: BookOpen, label: "Tutorials & FAQs" },
    ],
  },
  {
    color: "#EF4444", // red — Admin
    items: [
      { to: "/master-database", icon: Database, label: "Master DB", adminOnly: true },
      { to: "/super-admin", icon: ShieldCheck, label: "Command Center", superAdminOnly: true },
      { to: "/super-admin/parental-reports", icon: Shield, label: "Reports", superAdminOnly: true },
    ],
  },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  const [dueNotifications, setDueNotifications] = useState([]);
  const [communityUnread, setCommunityUnread] = useState(0);
  const [browseUnread, setBrowseUnread] = useState(0);
  const [tutorialsUnread, setTutorialsUnread] = useState(0);
  const [multiplayerUnread, setMultiplayerUnread] = useState(0);
  const [mdbBadge, setMdbBadge] = useState(0);
  const [newUsersBadge, setNewUsersBadge] = useState(0);
  const [parentalBadge, setParentalBadge] = useState(0);
  const [hiddenPages, setHiddenPages] = useState([]);
  const [hasMasterDbAccess, setHasMasterDbAccess] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('notificationSoundEnabled');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [lastNotificationIds, setLastNotificationIds] = useState(new Set());
  const location = useLocation();
  const navigate = useNavigate();
  const prevPathname = useRef(location.pathname);
  const notificationRef = useRef(null);
  const { user, logout } = useAuth();
  
  // Fetch user preferences including hidden pages
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const response = await axios.get(`${API}/settings/preferences`);
        setHiddenPages(response.data.hiddenPages || []);
      } catch (error) {
        // Silently fail
      }
    };
    fetchPreferences();
  }, []);
  
  // Check Master Database access for non-global-admins
  useEffect(() => {
    if (user && !user.isAdmin) {
      axios.get(`${API}/master/permissions/me`)
        .then(response => {
          // User has access if they have any permission level
          setHasMasterDbAccess(response.data.level !== null);
        })
        .catch(() => {
          setHasMasterDbAccess(false);
        });
    } else if (user?.isAdmin) {
      setHasMasterDbAccess(true);
    }
  }, [user]);
  
  // Filter nav groups based on hidden pages and admin status
  const filteredNavGroups = useMemo(() => {
    return navGroups
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (item.to === "/settings") return true;
          if (item.superAdminOnly) return user?.isSuperAdmin === true;
          if (item.adminOnly) {
            if (user?.isAdmin) return true;
            if (item.to === "/master-database" && hasMasterDbAccess) return true;
            return false;
          }
          if (item.hideKey && hiddenPages.includes(item.hideKey)) return false;
          // Notifications feature is deactivated for non-admins while testing continues.
          if (item.to === "/notifications" && !user?.isAdmin) return false;
          return true;
        }),
      }))
      .filter(group => group.items.length > 0);
  }, [hiddenPages, user?.isAdmin, user?.isSuperAdmin, hasMasterDbAccess]);
  
  // Check for due notifications.
  //
  // We deliberately read `soundEnabled` and `lastNotificationIds` from refs
  // inside the callback. If we depended on them directly the callback ref
  // would change on every response (because we `setLastNotificationIds`
  // inside), which would re-run the setup effect and refire an immediate
  // request — the previous shape produced an N+1 request storm on any
  // page with a slow renderer (500+ ms game info pages could queue 30+
  // /due XHRs).
  const soundEnabledRef = useRef(soundEnabled);
  const lastNotificationIdsRef = useRef(lastNotificationIds);
  useEffect(() => { soundEnabledRef.current = soundEnabled; }, [soundEnabled]);
  useEffect(() => { lastNotificationIdsRef.current = lastNotificationIds; }, [lastNotificationIds]);

  const checkNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notifications/check/due`);
      const notifications = response.data || [];

      const currentIds = new Set(notifications.map(n => n.id));
      const prevIds = lastNotificationIdsRef.current;
      const newNotifications = notifications.filter(n => !prevIds.has(n.id));

      if (newNotifications.length > 0 && soundEnabledRef.current && prevIds.size > 0) {
        playNotificationSound();
        newNotifications.forEach(n => {
          toast.info(`Reminder: ${n.name}`, {
            description: n.linkedItemName || n.description,
            duration: 10000,
          });
        });
      }

      setLastNotificationIds(currentIds);
      setDueNotifications(notifications);
    } catch (error) {
      // Silently fail - don't spam console
    }
  }, []);

  // Check notifications on mount and every 60 seconds
  useEffect(() => {
    checkNotifications();
    const interval = setInterval(checkNotifications, 60000);
    return () => clearInterval(interval);
  }, [checkNotifications]);

  // Toast when an admin resolves one of my platform/system requests
  const checkResolvedPlatformRequests = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/platform-system-requests/mine/resolved`);
      const resolved = data?.requests || [];
      if (resolved.length === 0) return;
      resolved.forEach((r) => {
        const kind = r.type === "system" ? "System" : "Platform";
        if (r.status === "added") {
          toast.success(`${kind} request approved`, {
            description: `"${r.name}" was added to the Master Database.`,
            duration: 10000,
          });
        } else {
          toast.info(`${kind} request declined`, {
            description: `Your request for "${r.name}" wasn't added this time.`,
            duration: 10000,
          });
        }
      });
      await axios.post(`${API}/platform-system-requests/mine/ack`, {
        ids: resolved.map((r) => r.id),
      });
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    checkResolvedPlatformRequests();
    const interval = setInterval(checkResolvedPlatformRequests, 90000);
    return () => clearInterval(interval);
  }, [checkResolvedPlatformRequests]);

  // Toast when someone replies to / mentions me, or an admin removes my comment
  const checkCommentNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/community/comment-notifications/mine`);
      const notes = data?.notifications || [];
      if (notes.length === 0) return;
      notes.forEach((n) => {
        if (n.type === "admin_removed") {
          toast.warning("Comment removed", { description: n.message, duration: 10000 });
        } else {
          toast.info("New community activity", { description: n.message, duration: 10000 });
        }
      });
      await axios.post(`${API}/community/comment-notifications/ack`, { ids: notes.map((n) => n.id) });
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    checkCommentNotifications();
    const interval = setInterval(checkCommentNotifications, 60000);
    return () => clearInterval(interval);
  }, [checkCommentNotifications]);

  // Poll community unread badge (news + library-matched content)
  const checkCommunityUnread = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/community/notifications/summary`);
      setCommunityUnread(response.data?.total || 0);
    } catch (error) {
      // Silently fail
    }
  }, []);

  useEffect(() => {
    checkCommunityUnread();
    const interval = setInterval(checkCommunityUnread, 60000);
    const onRefresh = () => checkCommunityUnread();
    window.addEventListener("community-unread-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("community-unread-refresh", onRefresh);
    };
  }, [checkCommunityUnread]);

  // Poll Browse (new games) + Tutorials (new tutorials) unread badges
  const checkOtherUnread = useCallback(async () => {
    try {
      const [b, t] = await Promise.all([
        axios.get(`${API}/master/browse/notifications/summary`).catch(() => ({ data: { total: 0 } })),
        axios.get(`${API}/tutorials/notifications/summary`).catch(() => ({ data: { total: 0 } })),
      ]);
      setBrowseUnread(b.data?.total || 0);
      setTutorialsUnread(t.data?.total || 0);
    } catch (error) { /* silent */ }
  }, []);

  // Poll Multiplayer "Discover Player" schedule-match badge
  const checkMultiplayerUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/multiplayer/notifications/summary`);
      setMultiplayerUnread(r.data?.total || 0);
    } catch (error) { /* silent */ }
  }, []);

  useEffect(() => {
    checkOtherUnread();
    const interval = setInterval(checkOtherUnread, 60000);
    const onRefresh = () => checkOtherUnread();
    window.addEventListener("unread-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("unread-refresh", onRefresh);
    };
  }, [checkOtherUnread]);

  // Poll multiplayer match badge + refresh on event
  useEffect(() => {
    checkMultiplayerUnread();
    const interval = setInterval(checkMultiplayerUnread, 60000);
    const onRefresh = () => checkMultiplayerUnread();
    window.addEventListener("unread-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("unread-refresh", onRefresh);
    };
  }, [checkMultiplayerUnread]);

  // Mark multiplayer matches seen when the user opens the Multiplayer page
  useEffect(() => {
    if (location.pathname.endsWith("/multiplayer")) {
      axios.post(`${API}/multiplayer/notifications/read-all`).then(() => {
        setMultiplayerUnread(0);
      }).catch(() => {});
    }
  }, [location.pathname]);

  // Poll admin sidebar badges: Master DB (coverage + platform requests + bug
  // reports) and super-admin (new users, parental reports).
  const checkAdminBadges = useCallback(async () => {
    if (user?.isAdmin || hasMasterDbAccess) {
      try {
        const r = await axios.get(`${API}/master/badge-counts`);
        setMdbBadge(r.data?.total || 0);
      } catch (e) { /* no access */ }
    }
    if (user?.isSuperAdmin) {
      try {
        const r = await axios.get(`${API}/super-admin/badges`);
        setNewUsersBadge(r.data?.newUsers || 0);
        setParentalBadge(r.data?.parentalReports || 0);
      } catch (e) { /* no access */ }
    }
  }, [user?.isAdmin, user?.isSuperAdmin, hasMasterDbAccess]);

  useEffect(() => {
    checkAdminBadges();
    const interval = setInterval(checkAdminBadges, 60000);
    const onRefresh = () => checkAdminBadges();
    window.addEventListener("unread-refresh", onRefresh);
    return () => {
      clearInterval(interval);
      window.removeEventListener("unread-refresh", onRefresh);
    };
  }, [checkAdminBadges]);

  // Clear the "new users" badge when the super admin opens Command Center
  useEffect(() => {
    if (user?.isSuperAdmin && location.pathname === "/super-admin") {
      axios.post(`${API}/super-admin/badges/seen`, { type: "new_users" })
        .then(() => setNewUsersBadge(0))
        .catch(() => {});
    }
  }, [location.pathname, user?.isSuperAdmin]);


  
  // Save sound preference
  useEffect(() => {
    localStorage.setItem('notificationSoundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);
  
  // Close notification dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Close sidebar when route changes (mobile) - intentional cascading render
  useEffect(() => {
    if (prevPathname.current !== location.pathname) {
      setSidebarOpen(false);
      prevPathname.current = location.pathname;
    }
  }, [location.pathname]);

  // Track page visits for super admin analytics (fire-and-forget)
  useEffect(() => {
    const page = location.pathname.replace(/^\/app/, "") || "/dashboard";
    axios.post(`${API}/super-admin/track`, {
      action: "page_visit",
      details: { page },
    }).catch(() => {});
  }, [location.pathname]);

  // Close sidebar when resizing or rotating (mobile)
  useEffect(() => {
    const handleResize = () => {
      // Close sidebar on md breakpoint or when in landscape on small height screens
      if (window.innerWidth >= 768 || (window.innerHeight < 500 && window.innerWidth > window.innerHeight)) {
        setSidebarOpen(false);
      }
    };
    
    const handleOrientationChange = () => {
      // Close sidebar when orientation changes to landscape on mobile
      setSidebarOpen(false);
    };
    
    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleOrientationChange);
    
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Mobile Header */}
      <div className="fixed top-0 left-0 right-0 z-40 md:hidden bg-[#0A0A0A] border-b border-white/5">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="flex flex-col items-center">
              <img src="/logo.png" alt="Game-o-Rang" className="w-14 h-14 object-contain" />
              <span className="px-1 py-0.5 text-[8px] font-bold bg-amber-500/20 text-amber-400 rounded border border-amber-500/30 -mt-1">ALPHA</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile Notification Bell */}
            <CommentActivityBell user={user} />
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="p-2 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors relative"
                data-testid="notification-bell-mobile"
              >
                <Bell className={`w-5 h-5 ${dueNotifications.length > 0 ? 'text-yellow-400' : ''}`} />
                {dueNotifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white animate-pulse">
                    {dueNotifications.length > 9 ? '9+' : dueNotifications.length}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 top-12 w-[calc(100vw-2rem)] sm:w-80 max-w-80 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden">
                  <div className="p-3 border-b border-white/10 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    <button
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      className="p-1.5 rounded hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title={soundEnabled ? "Mute sounds" : "Enable sounds"}
                    >
                      {soundEnabled ? <Volume2 className="w-4 h-4 text-green-400" /> : <VolumeX className="w-4 h-4 text-white/50" />}
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {dueNotifications.length === 0 ? (
                      <div className="p-4 text-center text-white/50 text-sm">
                        No pending notifications
                      </div>
                    ) : (
                      dueNotifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => {
                            setNotificationDropdownOpen(false);
                            navigate('/notifications');
                          }}
                          className="p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        >
                          <p className="font-medium text-white text-sm truncate">{notif.name}</p>
                          {notif.linkedItemName && (
                            <p className="text-white/50 text-xs truncate mt-0.5">{notif.linkedItemName}</p>
                          )}
                          <p className="text-purple-400 text-xs mt-1">
                            {notif.type === 'reminder' ? '⏰ Reminder' : notif.type === 'challenge' ? '🎯 Challenge' : '🔔 Alert'}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  {dueNotifications.length > 0 && (
                    <div className="p-2 border-t border-white/10">
                      <button
                        onClick={() => {
                          setNotificationDropdownOpen(false);
                          navigate('/notifications');
                        }}
                        className="w-full text-center text-sm text-purple-400 hover:text-purple-300 py-1"
                      >
                        View All Notifications
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-50
          w-64 border-r border-white/5 bg-[#0A0A0A] flex flex-col
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}
        `}
      >
        {/* Logo and Notification Bell (Desktop) */}
        <div className="p-4 border-b border-white/5">
          <div className="flex items-center justify-between">
            <div className="flex flex-col items-center">
              <img src="/logo.png" alt="Game-o-Rang" className="w-[80px] h-[80px] object-contain" />
              <span className="px-1.5 py-0.5 text-[10px] font-bold bg-amber-500/20 text-amber-400 rounded border border-amber-500/30 -mt-1">ALPHA</span>
            </div>
            
            {/* Desktop Notification Bell */}
            <CommentActivityBell user={user} />
            <div className="relative hidden md:block" ref={notificationRef}>
              <button
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="p-2 rounded-lg bg-white/5 text-white hover:bg-white/10 transition-colors relative"
                data-testid="notification-bell-desktop"
              >
                <Bell className={`w-5 h-5 ${dueNotifications.length > 0 ? 'text-yellow-400' : ''}`} />
                {dueNotifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white animate-pulse">
                    {dueNotifications.length > 9 ? '9+' : dueNotifications.length}
                  </span>
                )}
              </button>
              
              {/* Notification Dropdown (Desktop) */}
              {notificationDropdownOpen && (
                <div className="absolute left-0 top-12 w-80 bg-[#0A0A0A] border border-white/10 rounded-lg shadow-xl z-50 max-h-96 overflow-hidden">
                  <div className="p-3 border-b border-white/10 flex items-center justify-between">
                    <span className="font-medium text-white">Notifications</span>
                    <button
                      onClick={() => setSoundEnabled(!soundEnabled)}
                      className="p-1.5 rounded hover:bg-white/10 transition-colors"
                      title={soundEnabled ? "Mute sounds" : "Enable sounds"}
                    >
                      {soundEnabled ? <Volume2 className="w-4 h-4 text-green-400" /> : <VolumeX className="w-4 h-4 text-white/50" />}
                    </button>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {dueNotifications.length === 0 ? (
                      <div className="p-4 text-center text-white/50 text-sm">
                        No pending notifications
                      </div>
                    ) : (
                      dueNotifications.map((notif) => (
                        <div
                          key={notif.id}
                          onClick={() => {
                            setNotificationDropdownOpen(false);
                            navigate('/notifications');
                          }}
                          className="p-3 border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        >
                          <p className="font-medium text-white text-sm truncate">{notif.name}</p>
                          {notif.linkedItemName && (
                            <p className="text-white/50 text-xs truncate mt-0.5">{notif.linkedItemName}</p>
                          )}
                          <p className="text-purple-400 text-xs mt-1">
                            {notif.type === 'reminder' ? '⏰ Reminder' : notif.type === 'challenge' ? '🎯 Challenge' : '🔔 Alert'}
                          </p>
                        </div>
                      ))
                    )}
                  </div>
                  {dueNotifications.length > 0 && (
                    <div className="p-2 border-t border-white/10">
                      <button
                        onClick={() => {
                          setNotificationDropdownOpen(false);
                          navigate('/notifications');
                        }}
                        className="w-full text-center text-sm text-purple-400 hover:text-purple-300 py-1"
                      >
                        View All Notifications
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          <div className="space-y-1.5">
            {filteredNavGroups.map((group, gi) => (
              <div
                key={gi}
                className="relative pl-2.5 rounded-lg"
                style={{ backgroundColor: group.color + "18" }}
              >
                {/* Group color bar */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-[3px] rounded-full"
                  style={{ backgroundColor: group.color, opacity: 0.7 }}
                />
                <ul>
                  {group.items.map((item) => {
                    const hasBadgeSlot = ["/social", "/browse", "/tutorials", "/master-database", "/super-admin", "/super-admin/parental-reports"].includes(item.to);
                    return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.to === "/"}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                          `relative flex items-center gap-3 px-3 py-1.5 min-h-[32px] rounded-md transition-all duration-200 touch-manipulation ${
                            hasBadgeSlot ? "pr-9" : ""
                          } ${
                            isActive
                              ? "bg-white/10 text-white"
                              : "text-white/50 hover:text-white hover:bg-white/5"
                          }`
                        }
                      >
                        <div className="relative">
                          <item.icon className={`w-5 h-5 ${item.label === "Notifications" && dueNotifications.length > 0 ? "text-yellow-400" : ""}`} />
                          {item.label === "Notifications" && dueNotifications.length > 0 && (
                            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[9px] font-bold flex items-center justify-center text-white">
                              {dueNotifications.length > 9 ? '9+' : dueNotifications.length}
                            </span>
                          )}
                        </div>
                        <span className="font-medium">{item.label}</span>
                        {item.to === "/social" && communityUnread > 0 && (
                          <span
                            data-testid="community-unread-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {communityUnread > 99 ? '99+' : communityUnread}
                          </span>
                        )}
                        {item.to === "/browse" && browseUnread > 0 && (
                          <span
                            data-testid="browse-unread-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {browseUnread > 99 ? '99+' : browseUnread}
                          </span>
                        )}
                        {item.to === "/tutorials" && tutorialsUnread > 0 && (
                          <span
                            data-testid="tutorials-unread-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {tutorialsUnread > 99 ? '99+' : tutorialsUnread}
                          </span>
                        )}
                        {item.to === "/multiplayer" && multiplayerUnread > 0 && (
                          <span
                            data-testid="multiplayer-unread-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {multiplayerUnread > 99 ? '99+' : multiplayerUnread}
                          </span>
                        )}
                        {item.to === "/master-database" && mdbBadge > 0 && (
                          <span
                            data-testid="mdb-requests-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {mdbBadge > 99 ? '99+' : mdbBadge}
                          </span>
                        )}
                        {item.to === "/super-admin" && newUsersBadge > 0 && (
                          <span
                            data-testid="command-center-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {newUsersBadge > 99 ? '99+' : newUsersBadge}
                          </span>
                        )}
                        {item.to === "/super-admin/parental-reports" && parentalBadge > 0 && (
                          <span
                            data-testid="parental-reports-badge"
                            className="absolute right-2 top-1/2 -translate-y-1/2 min-w-[20px] h-5 px-1.5 bg-red-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white"
                          >
                            {parentalBadge > 99 ? '99+' : parentalBadge}
                          </span>
                        )}
                      </NavLink>
                    </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>

        {/* User Profile & Logout */}
        <div className="px-3 py-3 border-t border-white/5">
          {user && (
            <Link to="/account" className="flex items-center gap-3 mb-3 p-2 -m-2 rounded-lg hover:bg-white/5 transition-colors cursor-pointer" data-testid="account-settings-link">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-medium">
                  {user.name?.charAt(0) || user.email?.charAt(0) || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white font-medium truncate">{user.name}</p>
                <p className="text-xs text-white/40 truncate">{user.email}</p>
              </div>
              <UserCog className="w-4 h-4 text-white/30" />
            </Link>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-sm text-white/60 hover:text-white hover:bg-white/5 rounded-lg transition-colors min-h-[44px] touch-manipulation"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
          <p data-testid="app-version-label" className="text-xs text-white/30 text-center mt-3">{APP_VERSION}</p>
        </div>
      </aside>

      {/* Main Content — `overflow-x-clip` (not `hidden`) so wide inner
          content is clipped without establishing a scroll container, which
          would trap `position: sticky` children inside. */}
      <main className="flex-1 min-w-0 md:ml-64 overflow-x-clip">
        {/* Spacer for mobile header */}
        <div className="h-24 md:hidden" />
        <div className="p-4 md:p-6 lg:p-8 safe-area-bottom">
          <SubscriptionProvider>
            <Outlet />
          </SubscriptionProvider>
        </div>
        {/* Footer */}
        <footer className="border-t border-white/5 px-4 py-3 flex items-center justify-center gap-2 text-xs text-white/25">
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-white/40 transition-colors"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt=""
              className="w-3.5 h-3.5 rounded-sm"
            />
            Made with Emergent
          </a>
        </footer>
      </main>
    </div>
  );
}
